import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Card, GrainOverlay } from "@vb/ui";
import { useGame } from "./store.js";
import { rgs, setRgsAuthToken } from "./lib/rgs-client.js";
import { demoRgs } from "./lib/demo-rgs-client.js";
import { WsClient, deriveWsUrl, type WsResyncEvent } from "./lib/ws-client.js";
import { formatCryptoShort, truncateHash, roundTag } from "./lib/format.js";
import { usePublicFeed } from "./lib/public-feed.js";
import { TopBar } from "./components/TopBar.js";
import { LiveMultiplier } from "./components/LiveMultiplier.js";
import { LiveCashoutButton } from "./components/LiveCashoutButton.js";
import { VaultDoor } from "./components/cinematic/VaultDoor.js";
import { liveVaultState, type LivePhase } from "./components/cinematic/liveVaultState.js";
import { useTime } from "./components/cinematic/clock.js";
import { initAudio, setSoundMuted } from "./lib/sound.js";
import { BetInput } from "./components/BetInput.js";
import { HistoryStrip } from "./components/HistoryStrip.js";
import { LivePlayerFeed } from "./components/LivePlayerFeed.js";
import { ProvablyFairModal } from "./components/ProvablyFairModal.js";
import { VerifierOverlay } from "./components/VerifierOverlay.js";
import { WinScreen, BustScreen } from "./components/EndScreens.js";
import { track } from "./lib/analytics.js";

function useWsStream() {
  const token = useGame((s) => s.token);
  const demoMode = useGame((s) => s.demoMode);
  const applyWsEvent = useGame((s) => s.applyWsEvent);
  const setPhase = useGame((s) => s.setPhase);
  useEffect(() => {
    if (!token || demoMode) return;
    // Vercel rewrites can't proxy WebSocket upgrades. Build-time env
    // VITE_WS_BASE points the client directly at the game-server (e.g.
    // vaultbreaker-api.fly.dev or wss://vaultbreaker-api.fly.dev). The
    // helper forces the protocol to match the page (https→wss, http→ws)
    // so a misconfigured VITE_WS_BASE can't trigger a mixed-content error.
    const wsBase = (import.meta as unknown as { env: { VITE_WS_BASE?: string } })
      .env.VITE_WS_BASE;
    const url = deriveWsUrl(wsBase);

    // Reconnect-resync handler. The server fires `round:resync` immediately
    // after the auth message, carrying the live multiplier so the client
    // can paint the right state without waiting for the next tick (which
    // is the bug this whole change exists to fix). Wrapped in try/catch
    // because the handler reaches into store actions and a thrown error
    // here would tear down the WS pipeline.
    const onResync = (snap: WsResyncEvent) => {
      try {
        if (snap.phase === "error") {
          // eslint-disable-next-line no-console
          console.warn("[ws]", snap.error ?? "connection lost");
          setPhase("idle");
          return;
        }
        if (snap.phase === "idle") {
          // No active round — drop back to betting so the player can place a
          // new bet. Don't clobber win/bust cinematic that may be playing.
          const cur = useGame.getState().phase;
          if (cur === "live") setPhase("betting");
          return;
        }
        if (snap.phase === "live" && snap.roundId && snap.currentMultiplier) {
          // Push phase first so applyWsEvent's tick handler accepts the
          // multiplier (it gates on phase === "live"). Then dispatch a
          // synthetic tick to seed the multiplier display immediately —
          // the server's next real tick (~33ms later) will overwrite it.
          setPhase("live");
          applyWsEvent({
            type: "tick",
            roundId: snap.roundId,
            tMs: snap.elapsedMs ?? 0,
            multiplier: snap.currentMultiplier,
          });
          return;
        }
        if (snap.phase === "resolved") {
          // Missed the resolved event during disconnect. Refetch balance so
          // the wallet shown matches the server, then bounce phase to
          // betting so the cinematic doesn't re-trigger on stale data.
          const sid = useGame.getState().sessionId;
          if (sid) {
            void rgs.balance(sid).then((b) => {
              useGame.getState().setBalance(b.balance);
              setPhase("betting");
            }).catch(() => setPhase("betting"));
          } else {
            setPhase("betting");
          }
          return;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[ws] resync handler threw", err);
      }
    };

    const client = new WsClient({ url, token, onEvent: applyWsEvent, onResync });
    client.connect();
    return () => client.close();
  }, [token, demoMode, applyWsEvent, setPhase]);
}

/**
 * Idle/live vault on the game stage. Drives the new circular vault door
 * from store fields (phase + cracked + multiplier) via a continuous-time
 * hook so the dial breathes and the next-lock pulse animates. Door stays
 * closed here — the post-cashout SceneWin/SceneBust own the open transitions.
 */
function LiveVault({
  phase,
  cracked,
  multiplier,
  size = 320,
}: {
  phase: LivePhase;
  cracked: number;
  multiplier: number;
  size?: number;
}) {
  const t = useTime();
  const state = liveVaultState(t, phase, cracked, multiplier);
  // The door is rendered at a fixed pixel size; the wrapper is exactly that
  // square so the row above (multiplier) and below (LOCKS) sit flush.
  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
      }}
    >
      <VaultDoor state={state} size={size} />
    </div>
  );
}

// Comma-separated operator-parent origin allowlist. Empty (default) means
// the iframe accepts NO postMessages — closes the drive-by-balance-set hole.
// Operators embedding the iframe set this at build time.
const ALLOWED_PARENTS: string[] = (
  ((import.meta as unknown as { env: { VITE_ALLOWED_PARENTS?: string } }).env
    .VITE_ALLOWED_PARENTS ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
);

function useIframeHandshake() {
  const setBalance = useGame((s) => s.setBalance);
  const setOperator = useGame((s) => s.setOperator);
  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      // Origin allowlist gate. Without this, ANY page that iframes us could
      // postMessage SET_BALANCE and arbitrarily mutate the player wallet.
      if (!ALLOWED_PARENTS.includes(ev.origin)) return;
      if (!ev.data || typeof ev.data !== "object") return;
      const data = ev.data as { type?: string; value?: unknown; locale?: unknown };
      switch (data.type) {
        case "SET_BALANCE":
          if (typeof data.value === "number") setBalance(data.value);
          break;
        case "SET_OPERATOR":
          if (typeof data.value === "string") {
            setOperator(data.value, typeof data.locale === "string" ? data.locale : "en");
          }
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [setBalance, setOperator]);
}

export function GameView() {
  // Individual selectors — primitive/stable subscriptions. GameView no longer
  // re-renders 30×/s on multiplier ticks (that's moved into LiveMultiplier).
  const sessionId = useGame((s) => s.sessionId);
  const currency = useGame((s) => s.currency);
  const balance = useGame((s) => s.balance);
  const serverSeedHash = useGame((s) => s.serverSeedHash);
  const phase = useGame((s) => s.phase);
  const roundId = useGame((s) => s.roundId);
  const roundNonce = useGame((s) => s.roundNonce);
  const betAmount = useGame((s) => s.betAmount);
  const cashoutMultiplier = useGame((s) => s.cashoutMultiplier);
  const payout = useGame((s) => s.payout);
  const history = useGame((s) => s.history);
  const cracked = useGame((s) => s.cracked);
  const operatorId = useGame((s) => s.operatorId);
  const locale = useGame((s) => s.locale);
  const multiplierSnapshot = useGame((s) => s.multiplier); // only used for the bust-screen value, not ticking

  const hydrateFromLaunch = useGame((s) => s.hydrateFromLaunch);
  const applyOpenRound = useGame((s) => s.applyOpenRound);
  const applyResolved = useGame((s) => s.applyResolved);
  const setBet = useGame((s) => s.setBet);
  const resetAction = useGame((s) => s.reset);
  const clearSession = useGame((s) => s.clearSession);
  const refillDemoBalance = useGame((s) => s.refillDemoBalance);
  const storeGameSlug = useGame((s) => s.gameSlug);
  const setOperator = useGame((s) => s.setOperator);
  const setDemoMode = useGame((s) => s.setDemoMode);
  const demoMode = useGame((s) => s.demoMode);

  // Subscriptions for autoplay + muted (each is a primitive selector so the
  // component re-renders only when the field actually changes).
  const muted = useGame((s) => s.muted);
  const fairnessOverlay = useGame((s) => s.fairnessOverlay);
  const autoplayActive = useGame((s) => s.autoplay.active);
  const autoplayRemaining = useGame((s) => s.autoplay.remaining);
  const autoplayPaused = useGame((s) => s.autoplay.paused);
  const pendingAutoCashout = useGame((s) => s.autoplay.pendingAutoCashout);
  const setCashoutInFlight = useGame((s) => s.setCashoutInFlight);
  const acknowledgeAutoCashout = useGame((s) => s.acknowledgeAutoCashout);
  const resolveAutoplay = useGame((s) => s.resolveAutoplay);
  const stopAutoplay = useGame((s) => s.stopAutoplay);

  const [fairOpen, setFairOpen] = useState(false);
  const [fairRoundId, setFairRoundId] = useState<string | null>(null);
  const [lastResolvedRoundId, setLastResolvedRoundId] = useState<string | null>(null);
  const [booting, setBooting] = useState(true);
  const feed = usePublicFeed();
  // bustSeq increments on every bust so the Vault can be remounted with a fresh key
  // (CSS animation only plays when the element mounts).
  const [bustSeq, setBustSeq] = useState(0);

  useWsStream();
  useIframeHandshake();

  // Bootstrap a session on mount (StrictMode will fire twice; the sessionId guard prevents a 2nd launch).
  // If the player navigated here from PP/CC the store still holds a session
  // bound to that other gameSlug — server 400s on slug mismatch, so wipe the
  // session and re-launch as vault-breaker.
  useEffect(() => {
    if (sessionId && storeGameSlug === "vault-breaker") {
      setBooting(false);
      return;
    }
    if (sessionId && storeGameSlug !== "vault-breaker") {
      clearSession();
    }
    const params = new URLSearchParams(location.search);
    // Default to the built-in "demo" operator so the public /play page always
    // works without an API key. Real operators override via ?operator=…
    const opId = params.get("operator") ?? "demo";
    const loc = params.get("locale") ?? locale;
    const cur =
      (params.get("currency") as "BTC" | "ETH" | "SOL" | "USDT" | "USD" | null) ?? "BTC";
    setOperator(opId, loc);

    const launchArgs = {
      operatorId: opId,
      playerId: `demo_${Math.random().toString(36).slice(2, 8)}`,
      currency: cur,
      locale: loc,
      // /play is the original crash game. Pressure Plate and Cipher Crack
      // bootstrap their sessions from their own components with their slug.
      gameSlug: "vault-breaker" as const,
    };

    rgs
      .launch(launchArgs)
      .then((res) => {
        hydrateFromLaunch(res);
        setBooting(false);
        track("session_launched", { gameSlug: "vault-breaker" });
      })
      .catch(async (err) => {
        // eslint-disable-next-line no-console
        console.error("launch failed", err);
        // DEV-only fallback: when the local game-server is unreachable,
        // hydrate from the demo client so the new vault renders with a
        // real-looking balance. Never falls through in production —
        // surfaces the connection error to whatever error UI eventually
        // gets wired up.
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn("[demo-mode] backend unreachable — using demo client");
          try {
            const res = await demoRgs.launch(launchArgs);
            hydrateFromLaunch(res);
            setDemoMode(true);
            setBooting(false);
          } catch (e2) {
            // eslint-disable-next-line no-console
            console.error("demo launch failed", e2);
          }
        } else {
          setBooting(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track bust transitions so the Vault remounts and its shudder animation replays.
  useEffect(() => {
    if (phase === "bust") setBustSeq((n) => n + 1);
  }, [phase]);

  // Auto-dismiss so the loop keeps moving. Win needs to let the 7.2s
  // ChestOpening cinematic play through; bust uses the shorter red takeover.
  // When autoplay is running with skipAnimations, we cut to ~250ms so the
  // loop flies through rounds at server tempo.
  const skipAutoplayCinematic = useGame((s) => s.autoplayConfig.skipAnimations);
  useEffect(() => {
    if (phase !== "win" && phase !== "bust") return;
    const skip = autoplayActive && skipAutoplayCinematic;
    const delay = skip ? 250 : phase === "win" ? 9500 : 4500;
    const id = setTimeout(() => resetAction(), delay);
    return () => clearTimeout(id);
  }, [phase, resetAction, autoplayActive, skipAutoplayCinematic]);

  // Sync muted state to the sound module. Sound is a singleton; this is the
  // only writer. Runs once on mount + on every toggle.
  useEffect(() => {
    setSoundMuted(muted);
  }, [muted]);

  // Sync session JWT to the rgs-client so round-route requests carry
  // Authorization: Bearer <jwt> automatically.
  const sessionToken = useGame((s) => s.token);
  useEffect(() => {
    setRgsAuthToken(sessionToken);
  }, [sessionToken]);

  // If autoplay is left running when the page unloads, stop it cleanly.
  useEffect(() => {
    const onUnload = () => {
      if (useGame.getState().autoplay.active) stopAutoplay("Page unloaded");
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [stopAutoplay]);

  const placeBet = useCallback(async () => {
    if (!sessionId) return;
    // Browser autoplay policy: AudioContext must be unlocked from a user
    // gesture. PLACE BET is the first guaranteed click; subsequent calls no-op.
    initAudio();
    try {
      const client = demoMode ? demoRgs : rgs;
      const res = await client.open({ sessionId, betAmount });
      applyOpenRound(res);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("open round failed", err);
    }
  }, [sessionId, betAmount, applyOpenRound, demoMode]);

  const cashout = useCallback(async () => {
    if (!sessionId || !roundId) return;
    setCashoutInFlight(true);
    try {
      // Snapshot multiplier at the moment of the click, for server send.
      const current = useGame.getState().multiplier;
      const client = demoMode ? demoRgs : rgs;
      const res = await client.resolve({
        sessionId,
        roundId,
        gameSlug: "vault-breaker",
        cashoutMultiplier: current,
      });
      applyResolved(res);
      // NB: lastResolvedRoundId is set by the phase-transition effect below,
      // not here — that handler covers BOTH cashout and bust paths from a
      // single source of truth.
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("cashout failed", err);
    } finally {
      setCashoutInFlight(false);
    }
  }, [sessionId, roundId, applyResolved, demoMode, setCashoutInFlight]);

  // lastResolvedRoundId is now driven by the phase-transition effect, so the
  // local wrapper just delegates to the store action.
  const reset = resetAction;

  // ---- Autoplay continuation refs --------------------------------------
  // Both `placeBet` (a useCallback dependent on betAmount) and the cycle
  // tracker need to be read from inside the autoplay-loop effect WITHOUT
  // appearing in its dep array. If they did, `resolveAutoplay`'s setBet()
  // would change `placeBet`'s identity, the effect would re-run, the
  // cleanup would fire `clearTimeout`, and the pending placeBet would be
  // canceled — autoplay would stop after exactly one round. Refs keep the
  // effect's identity stable across those mutations.
  const placeBetRef = useRef(placeBet);
  useEffect(() => {
    placeBetRef.current = placeBet;
  }, [placeBet]);
  const lastAutoCycleRef = useRef<string | null>(null);

  // Snapshots the round outcome the moment phase flips to "win" or "bust",
  // BEFORE the auto-dismiss timer fires `resetAction()` and clears
  // `cashoutMultiplier`/`payout`. Reading these out of the live store from
  // the continuation effect would always see post-reset zeros — every win
  // would be misclassified as a bust.
  const lastResolutionRef = useRef<{
    roundId: string;
    outcome: "win" | "bust";
    payoutDelta: number;
  } | null>(null);
  useEffect(() => {
    if ((phase === "win" || phase === "bust") && roundId) {
      const s = useGame.getState();
      const outcome: "win" | "bust" = s.cashoutMultiplier !== null ? "win" : "bust";
      const stake = s.autoplay.currentBet;
      const payoutDelta = outcome === "win" ? s.payout - stake : -stake;
      lastResolutionRef.current = { roundId, outcome, payoutDelta };
      setLastResolvedRoundId(roundId);
    }
  }, [phase, roundId]);

  // Auto-cashout dispatcher. The store sets `pendingAutoCashout = true`
  // inside `applyWsEvent` `case "tick"` when the multiplier crosses target;
  // we observe that here and call the existing cashout() so async/promise
  // logic stays out of the reducer.
  useEffect(() => {
    if (!pendingAutoCashout) return;
    if (phase !== "live") {
      acknowledgeAutoCashout();
      return;
    }
    acknowledgeAutoCashout();
    void cashout();
  }, [pendingAutoCashout, phase, cashout, acknowledgeAutoCashout]);

  // Autoplay loop: when a round resolves and the auto-dismiss timer returns
  // us to "betting" (with roundId cleared), apply the strategy step and
  // place the next bet. The roundId === null guard distinguishes "just reset"
  // from "applyOpenRound just set new round".
  useEffect(() => {
    if (!autoplayActive || autoplayPaused) return;
    if (phase !== "betting") return;
    if (roundId !== null) return; // mid-bet placement, not the post-cinematic gap
    if (!sessionId) return;
    if (!lastResolvedRoundId || lastResolvedRoundId === lastAutoCycleRef.current) return;
    lastAutoCycleRef.current = lastResolvedRoundId;

    // Outcome was captured by the phase-transition effect BEFORE the store
    // was reset. Reading it from the live store here would see the cleared
    // cashoutMultiplier/payout and misclassify wins.
    const resolution = lastResolutionRef.current;
    if (!resolution || resolution.roundId !== lastResolvedRoundId) return;
    const next = resolveAutoplay(resolution.outcome, resolution.payoutDelta);
    if (next === null) return; // autoplay stopped (limits or out of rounds)

    // Place the next bet on the next tick — gives state machinery a beat.
    const id = setTimeout(() => {
      if (!useGame.getState().autoplay.active) return;
      void placeBetRef.current();
    }, 60);
    return () => clearTimeout(id);
    // NB: deliberately omit placeBetRef + lastAutoCycleRef from deps; both
    // are mutated inside the effect's downstream actions and re-running
    // would cancel the in-flight timeout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phase,
    roundId,
    autoplayActive,
    autoplayPaused,
    sessionId,
    lastResolvedRoundId,
    resolveAutoplay,
  ]);

  // Reset the autoplay-cycle tracker when autoplay starts (so the very first
  // round after START doesn't get skipped on the lastResolvedRoundId match).
  useEffect(() => {
    if (!autoplayActive || lastResolvedRoundId !== null) return;
    // First-cycle: kick off an immediate placeBet. The bet is already
    // pre-loaded from `startAutoplay` in the store.
    const id = setTimeout(() => {
      if (useGame.getState().autoplay.active && useGame.getState().phase === "betting") {
        void placeBetRef.current();
      }
    }, 60);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplayActive, lastResolvedRoundId]);

  // Clear the cycle tracker when autoplay turns off so the NEXT start doesn't
  // skip its first round on a stale match.
  useEffect(() => {
    if (!autoplayActive) lastAutoCycleRef.current = null;
  }, [autoplayActive]);

  const nonceTag = useMemo(() => roundTag(roundNonce), [roundNonce]);
  const stageHidden = phase === "win" || phase === "bust";
  const canBet = sessionId && balance >= betAmount;
  const insufficient = !!sessionId && balance < betAmount;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-0)", color: "var(--fg-1)" }}>
      <GrainOverlay />
      <TopBar
        balance={balance}
        currency={currency}
        onFairClick={() => {
          if (!serverSeedHash) return;
          setFairOpen(true);
        }}
      />

      <main
        className="vb-game-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 10,
          padding: 10,
          maxWidth: 1280,
          margin: "0 auto",
          gridTemplateAreas: '"stage" "controls" "history" "feed"',
        }}
      >
        <Card
          style={{
            gridArea: "stage",
            padding: 12,
            position: "relative",
            overflow: "hidden",
          }}
          aria-label="Game stage"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Badge kind="live" style={{ padding: "2px 8px", fontSize: 9 }}>
                {phase === "live" ? "LIVE" : phase === "bust" ? "BUST" : "READY"}
              </Badge>
              <span className="label" style={{ fontSize: 10 }}>
                Round {nonceTag}
              </span>
            </div>
            <span
              className="mono"
              style={{ color: "var(--fg-3)", fontSize: 10 }}
              title={serverSeedHash ?? undefined}
            >
              seed {truncateHash(serverSeedHash ?? "", 4, 4)}
            </span>
          </div>

          {/* Stage body hidden under win/bust overlays — EndScreens own that moment. */}
          <div
            style={{ visibility: stageHidden ? "hidden" : "visible" }}
            aria-hidden={stageHidden}
          >
            {booting && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  display: "grid",
                  placeItems: "center",
                  height: 320,
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  letterSpacing: 1.5,
                  color: "var(--fg-3)",
                  textTransform: "uppercase",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: "var(--gold-bright)",
                    boxShadow: "0 0 12px var(--gold-bright)",
                    animation: "pp-bump 900ms ease-in-out infinite",
                  }}
                />
                <span>Connecting to vault…</span>
              </div>
            )}
            {!booting && (
              <div className="vb-stage-body">
                <div className="vb-stage-mult-top">
                  <LiveMultiplier />
                </div>
                <div className="vb-stage-vault">
                  <LiveVault
                    key={`vault-${bustSeq}`}
                    phase={phase}
                    cracked={cracked}
                    multiplier={multiplierSnapshot}
                  />
                </div>
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "center",
                marginTop: 6,
                alignItems: "center",
              }}
            >
              <span className="label" style={{ fontSize: 10 }}>
                LOCKS
              </span>
              {[0, 1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: i < cracked ? "var(--gold-bright)" : "var(--bg-3)",
                    border:
                      i < cracked ? "1px solid #8A6426" : "1px solid var(--line-strong)",
                    boxShadow: i < cracked ? "0 0 8px rgba(245,197,109,0.6)" : "none",
                  }}
                />
              ))}
            </div>
          </div>

          {phase === "win" &&
            cashoutMultiplier !== null &&
            !(autoplayActive && skipAutoplayCinematic) && (
              <WinScreen
                multiplier={cashoutMultiplier}
                payout={payout - betAmount}
                currency={currency}
                stake={betAmount}
                onDone={reset}
              />
            )}
          {phase === "bust" && !(autoplayActive && skipAutoplayCinematic) && (
            <BustScreen
              multiplier={multiplierSnapshot}
              stake={betAmount}
              currency={currency}
              onDone={reset}
            />
          )}
        </Card>

        <Card
          // Sticky to the bottom of the viewport so PLACE BET / CASH OUT is
          // always visible even when /play is iframed inside /casino at a
          // short height. Without this, players had to scroll past the
          // ~380px stage card to reach the bet controls.
          style={{
            gridArea: "controls",
            padding: 12,
            position: "sticky",
            bottom: 8,
            zIndex: 5,
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            background: "rgba(11,14,22,0.92)",
          }}
          className="vb-cashout-pin"
          aria-label="Game controls"
        >
          <div className="vb-controls-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <BetInput
                amount={betAmount}
                currency={currency}
                disabled={phase === "live"}
                onChange={setBet}
                maxBalance={balance + betAmount}
              />
            </div>
            <div className="vb-controls-cta">
              {(phase === "idle" || phase === "betting") &&
                !autoplayActive &&
                insufficient && (
                  <button
                    className="vb-btn vb-btn-gold"
                    onClick={() => {
                      track("refill_demo_balance", { gameSlug: "vault-breaker" });
                      void refillDemoBalance("vault-breaker");
                    }}
                    style={{ width: "100%", padding: "12px 18px", fontSize: 14 }}
                  >
                    ↻ REFILL DEMO BALANCE
                  </button>
                )}
              {(phase === "idle" || phase === "betting") &&
                !autoplayActive &&
                !insufficient && (
                  <button
                    className="vb-btn vb-btn-gold"
                    onClick={placeBet}
                    disabled={!canBet}
                    style={{ width: "100%", padding: "12px 18px", fontSize: 14 }}
                  >
                    PLACE BET · {betAmount.toFixed(currency === "BTC" ? 6 : 2)} {currency}
                  </button>
                )}
              {(phase === "idle" || phase === "betting") && autoplayActive && (
                <div
                  className="mono"
                  style={{
                    width: "100%",
                    padding: "12px 18px",
                    fontSize: 12,
                    textAlign: "center",
                    color: "var(--cyan)",
                    border: "1px solid var(--line-strong)",
                    borderRadius: 8,
                    background: "rgba(0,229,255,0.08)",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  Autoplay · placing next bet
                </div>
              )}
              {phase === "live" && <LiveCashoutButton onClick={cashout} />}
              {(phase === "win" || phase === "bust") && !autoplayActive && (
                <button
                  className="vb-btn"
                  onClick={reset}
                  style={{ width: "100%", padding: "12px 18px", fontSize: 14 }}
                >
                  NEXT ROUND
                </button>
              )}
            </div>
          </div>

          {insufficient && (phase === "idle" || phase === "betting") && (
            <div
              role="status"
              style={{
                marginTop: 8,
                fontSize: 11,
                color: "var(--red)",
                fontFamily: "var(--font-mono)",
              }}
            >
              Insufficient balance · current {formatCryptoShort(balance, currency)} {currency}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--fg-3)",
              paddingTop: 8,
              marginTop: 8,
              borderTop: "1px solid var(--line)",
            }}
          >
            <span>house edge · 1.0%</span>
            <span>round · 30s</span>
            <span>RTP · 99.0%</span>
          </div>
        </Card>

        <Card
          style={{ gridArea: "history", padding: "10px 12px" }}
          aria-label="Recent rounds"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="label" style={{ fontSize: 10, flexShrink: 0 }}>
              Recent · {history.length}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <HistoryStrip
                results={history}
                inline
                onSelect={(entry) => {
                  if (!entry.roundId) return;
                  setFairRoundId(entry.roundId);
                  setFairOpen(true);
                }}
              />
            </div>
          </div>
        </Card>

        <aside
          style={{ gridArea: "feed", display: "flex", flexDirection: "column", gap: 10 }}
          aria-label="Live feed"
        >
          <Card
            className="vb-rail-mult"
            style={{ padding: "10px 12px" }}
            aria-label="Live multiplier"
          >
            <LiveMultiplier size="compact" />
          </Card>
          <LivePlayerFeed rows={feed} roundTag={nonceTag} maxHeight={420} />
        </aside>
      </main>

      <ProvablyFairModal
        open={fairOpen}
        onClose={() => {
          setFairOpen(false);
          setFairRoundId(null);
        }}
        roundId={fairRoundId ?? lastResolvedRoundId ?? roundId}
        serverSeedHash={serverSeedHash}
      />

      {fairnessOverlay && (
        <VerifierOverlay
          lastResolvedRoundId={lastResolvedRoundId}
          onVerify={(rid) => {
            if (!rid) return;
            setFairRoundId(rid);
            setFairOpen(true);
          }}
        />
      )}
    </div>
  );
}
